import { MiniFace } from "./MiniFace";
import { RateGame, ReportProblem } from "./Feedback";
import { useEffect, useState, type ReactNode } from "react";
import { CLOAK_COLOURS, silhouetteUrl, formatPot, PHASE_LABELS, photoOf, secondsLeft, wording } from "@unfaithful/client";
import { clientPack as corePack } from "@unfaithful/content/client";
import { ACCUSE_CHOICES, floorNow, gatheringAt, HOT_SEAT_BY_ID, MOST_LIKELY_BY_ID, GRILL_BY_ID, GRILL_LIMIT, GRILL_QUESTIONS, grillText, SHIELD_INFO, phaseDurationMs, TIER_INFO, TIERS, type DiaryUploadRequest, type DiaryUploadTicket, type GameActionInput, type PlayerView, type PublicPlayer } from "@unfaithful/shared";
import { DiaryRecorder } from "./DiaryRecorder";
import { voiceLine } from "./voiceLine";
import { PlayPanel } from "./PlayPanel";

/**
 * The phone during a game (Step 2 prototype): your secret role, the phase, and
 * the one thing you can do right now. Step 5 gives each phase its own screen.
 *
 * Privacy rules from the spec:
 *  - Your role is hidden until you press and hold, so neighbours can't glance at it.
 *  - Faithful and Traitor phones show the same layouts at night, so nobody
 *    gives themselves away by tapping (Faithfuls get a harmless decoy task).
 */
export function GamePanel({
  view,
  clockOffset,
  act,
  requestUpload,
  onLeave,
  onEnd,
  voice,
}: {
  view: PlayerView;
  clockOffset: number;
  act: (action: GameActionInput) => Promise<string | null>;
  requestUpload: (req: DiaryUploadRequest) => Promise<DiaryUploadTicket | string>;
  onLeave?: () => void;
  onEnd?: () => void;
  /** The voice line, for guests playing from elsewhere. */
  voice?: React.ReactNode;
}) {
  const { castle, me, secrets } = view;
  const game = castle.game!;
  const [message, setMessage] = useState<string | null>(null);
  const left = useCountdown(castle.phaseEndsAt, clockOffset);
  const name = (id: string | null | undefined) => castle.players.find((p) => p.id === id)?.name ?? "Someone";
  const alive = castle.players.filter((p) => p.status === "alive");
  const others = alive.filter((p) => p.id !== me.id);
  const run = async (action: GameActionInput) => setMessage(await act(action));
  const isAlive = me.status === "alive";
  const isTraitor = me.role === "traitor";

  useEffect(() => setMessage(null), [castle.phase]);
  // The voice line goes quiet at night.
  useEffect(() => voiceLine.setHushed(castle.phase === "night"), [castle.phase]);

  // The Host's controls: in the page under the header (and inside the night's dark screen).
  const hostRow =
    me.isHost && castle.phase !== "finale" ? (
        <section className="host-row" role="group" aria-label="Host controls">
          <span className="host-row-label">
            <span className="host-crown" aria-hidden>♛</span> Host
          </span>
          <div className="host-row-buttons">
            <button className="host-btn" onClick={() => run({ type: game.paused ? "resume" : "pause" })}>
              {game.paused ? "Resume" : "Pause"}
            </button>
            <button className="host-btn" disabled={game.paused} onClick={() => run({ type: "extend", seconds: 60 })}>
              +1 min
            </button>
            <button className="host-btn is-strong" onClick={() => run({ type: "advance" })}>
              Move on
            </button>
          </div>
        </section>
    ) : null;

  return (
    <main className="screen game">
      <header className="game-top">
        <p className="game-phase">
          {game.day > 0 && <span className="game-day">Day {game.day} · </span>}
          {PHASE_LABELS[castle.phase]}
        </p>
        {left !== null && <p className="game-clock">{left}s</p>}
      </header>
      {hostRow}

      <RoleCard view={view} name={name} />
      {isAlive && castle.phase !== "finale" && <PotLine pot={game.pot} alive={alive.length} traitorsWithMe={isTraitor ? 1 + secrets.fellowTraitors.length : null} />}
      {game.paused && <p className="paused-note" role="status">Paused by the Host.</p>}

      {!isAlive && castle.phase !== "finale" ? (
        <section className="panel gallery-panel">
          <h2 className="panel-title">You're in the Gallery</h2>
          <p className="quiet">You were {me.status === "murdered" ? wording(game.settings.familyMode).murdered : me.status}. Watch, enjoy the drama, and keep quiet about the game.</p>
        </section>
      ) : (
        <section className="panel" aria-live="polite">
          {(() => {
            switch (castle.phase) {
              case "role-reveal":
                return game.done.includes(me.id) ? (
                  <p className="quiet">Waiting for everyone else to see their role.</p>
                ) : (
                  <button className="primary wax-button" onClick={() => run({ type: "ready" })}>
                    I've seen my role
                  </button>
                );

              case "mission":
                if (game.play) return <PlayPanel view={view} clockOffset={clockOffset} run={run} />;
                return (
                  <>
                    <p className="panel-lead">{corePack.missions.find((m) => m.id === game.mission?.id)?.title ?? "The mission"}</p>
                    <p className="quiet">{corePack.missions.find((m) => m.id === game.mission?.id)?.summary}</p>
                    {isTraitor && !game.mission?.result && (
                      <button className="secondary sabotage" disabled={secrets.missionSabotaged === true} onClick={() => run({ type: "mission-sabotage" })}>
                        {secrets.missionSabotaged ? "The mission has been sabotaged" : "Secretly sabotage this mission"}
                      </button>
                    )}
                    {me.isHost && !game.mission?.result && <MissionReport players={alive} run={run} />}
                    {game.mission?.result && (
                      <p className="quiet">
                        {game.mission.result.tier ? `${TIER_INFO[game.mission.result.tier].label}: ` : "Reported: "}+{formatPot(game.mission.result.potAdded)}
                      </p>
                    )}
                  </>
                );

              case "shield":
                return <ShieldChoice view={view} run={run} name={name} />;

              case "gathering":
                return <Drinks view={view} clockOffset={clockOffset} run={run} />;

              case "diaries": {
                const found = corePack.diaryPrompts.find((p) => p.id === secrets.diaryPromptId);
                const prompt = found?.text;
                const shieldNote = <ShieldOutcome view={view} />;
                if (!game.expected.includes(me.id) && !game.done.includes(me.id)) {
                  const going = game.expected.map(name);
                  return (
                    <>
                      {shieldNote}
                      <p className="panel-lead">Not you this time.</p>
                      <p className="quiet">
                        {going.length > 0 ? `${going.join(" and ")} ${going.length === 1 ? "is" : "are"} in the diary room. ` : ""}
                        Talk among yourselves until they're back. Your turn will come.
                      </p>
                    </>
                  );
                }
                return game.done.includes(me.id) ? (
                  <>
                    {shieldNote}
                    <p className="quiet">{found?.sealed ? "Sealed. Nobody sees it until the finale." : "Diary sent. Wait for the others."}</p>
                  </>
                ) : (
                  <>
                    {shieldNote}
                    <DiaryRoom>
                      <DiaryRecorder prompt={prompt ?? "Say what you really think."} requestUpload={requestUpload} act={act} />
                    </DiaryRoom>
                  </>
                );
              }

              case "vote": {
                const canVote = game.expected.includes(me.id);
                if (secrets.myVote) return <p className="panel-lead">You voted for {name(secrets.myVote)}. Your vote is locked.</p>;
                if (!canVote) return <p className="quiet">You're tied, so you can't vote in this revote. Plead your case.</p>;
                const pool = game.voteCandidates ? others.filter((p) => game.voteCandidates!.includes(p.id)) : others;
                return <PlayerChoice title="Who do you want to banish?" players={pool} onChoose={(id) => run({ type: "vote", targetId: id })} />;
              }

              case "night": {
                // Every phone does the same three steps: choose a face, light the candle, sleep.
                // The night waits for everyone, so nobody can tell who is murdering.
                let step1: React.ReactNode;
                let chosen = false;
                if (secrets.recruit?.offerToMe) {
                  step1 = <RecruitOffer answer={secrets.recruit.answer} run={run} />;
                  chosen = secrets.recruit.answer !== null;
                } else if (isTraitor && secrets.recruit?.targetId) {
                  step1 = (
                    <>
                      <p className="panel-lead">You've asked {name(secrets.recruit.targetId)} to join you.</p>
                      <p className="night-hint">
                        {secrets.recruit.answer === null
                          ? "Their answer will come before morning."
                          : secrets.recruit.answer === "accept"
                            ? "They said yes. From breakfast, they're one of you."
                            : "They refused. They don't know who asked, but they know you're out there."}
                      </p>
                    </>
                  );
                  chosen = true;
                } else if (isTraitor) {
                  const mine = secrets.murderVotes?.[me.id] ?? null;
                  step1 = (
                    <>
                      <PlayerChoice
                        title="Choose a face."
                        players={alive.filter((p) => p.id !== me.id && !secrets.fellowTraitors.includes(p.id) && p.connected)}
                        chosen={mine}
                        onChoose={(id) => run({ type: "murder-vote", targetId: id })}
                      />
                      <p className="night-hint">
                        {secrets.recruit ? "The one you'd bring over to the Unfaithful. You must all agree." : "The one who leaves the castle tonight. You must all agree."}
                        {secrets.fellowTraitors.length > 0 &&
                          ` ${secrets.fellowTraitors.map((t) => `${name(t)}: ${secrets.murderVotes?.[t] ? name(secrets.murderVotes[t]) : "not yet"}`).join(". ")}.`}
                      </p>
                    </>
                  );
                  chosen = !!mine;
                } else {
                  step1 = null; // NightTask shows the same face list to Faithful
                }
                return (
                  <NightShroud>
                    {hostRow}
                    <NightTask asleep={secrets.asleep} chosen={chosen} decoy={step1 === null ? others : null} onSleep={() => run({ type: "sleep" })}>
                      {step1}
                    </NightTask>
                  </NightShroud>
                );
              }

              case "endgame":
                return secrets.myEndgameVote ? (
                  <p className="panel-lead">You chose to {secrets.myEndgameVote === "end" ? "end the game" : "banish again"}.</p>
                ) : (
                  <>
                    <p className="panel-lead">End the game now, or banish again?</p>
                    <button className="primary wax-button" onClick={() => run({ type: "endgame-vote", choice: "end" })}>
                      End the game
                    </button>
                    <button className="secondary moon-button" onClick={() => run({ type: "endgame-vote", choice: "banish" })}>
                      Banish again
                    </button>
                  </>
                );

              case "finale": {
                const r = game.result;
                if (!r) return null;
                const won = r.winners.includes(me.id);
                return (
                  <>
                    <p className={`panel-lead winner-${r.winner}`}>{r.winner === "traitor" ? "The Unfaithful win." : "The Faithful win."}</p>
                    <p className="quiet">{won ? `You share ${formatPot(r.pot)}.` : "Not this time."}</p>
                    <RateGame castle={castle.code} />
                  </>
                );
              }

              case "round-table":
                return <RoundTable view={view} clockOffset={clockOffset} name={name} run={run} />;

              case "breakfast":
                if (secrets.recruit?.recruitedMe) {
                  return (
                    <div className="recruited">
                      <p className="panel-lead">You're Unfaithful now.</p>
                      <p className="quiet">
                        Your fellow Unfaithful: {secrets.fellowTraitors.map(name).join(", ") || "none left"}. From tonight you choose with them, and you win with them. Walk into breakfast like nothing happened.
                      </p>
                    </div>
                  );
                }
                return (
                  <>
                    <ShieldOutcome view={view} />
                    <p className="quiet">Look up at the TV.</p>
                  </>
                );

              default:
                return <p className="quiet">Look up at the TV.</p>;
            }
          })()}
        </section>
      )}

      {message && <p className="toast" role="status">{message}</p>}


      {voice}
      <div className="game-exit">
        <ReportProblem />
        {me.isHost ? (
          <button className="text-button" onClick={() => confirm("End this game for everyone? It can't be undone.") && onEnd?.()}>
            End the game for everyone
          </button>
        ) : (
          <button className="text-button" onClick={() => confirm("Leave this game? You can't rejoin it.") && onLeave?.()}>
            Leave the game
          </button>
        )}
      </div>
    </main>
  );
}

/**
 * The pot, and what you'd take home if your side won right now. A Traitor knows
 * exactly how many share it; a Faithful only knows it's at least this much.
 */
function PotLine({ pot, alive, traitorsWithMe }: { pot: number; alive: number; traitorsWithMe: number | null }) {
  // What you'd take home depends on your side, so it's hidden like your role: hold to see it.
  const [peek, setPeek] = useState(false);
  const share = traitorsWithMe ? Math.floor(pot / traitorsWithMe / 50) * 50 : Math.floor(pot / Math.max(1, alive) / 50) * 50;
  return (
    <section
      className="pot-line"
      aria-label="The prize pot"
      onPointerDown={() => setPeek(true)}
      onPointerUp={() => setPeek(false)}
      onPointerLeave={() => setPeek(false)}
      onPointerCancel={() => setPeek(false)}
      onContextMenu={(e) => e.preventDefault()}
    >
      <span className="pot-coin" aria-hidden />
      <div>
        <p className="pot-total">{formatPot(pot)} <span>in the pot</span></p>
        <p className="pot-share">
          {pot === 0
            ? "Win missions together to fill it."
            : !peek
              ? "Press and hold to see your share."
              : traitorsWithMe
                ? `If the Unfaithful won now, you'd take home ${formatPot(share)}.`
                : `If the Faithful won now, you'd take home at least ${formatPot(share)}.`}
        </p>
      </div>
    </section>
  );
}

/** Press and hold to see your role; it hides again when you let go. */
function RoleCard({ view, name }: { view: PlayerView; name: (id: string) => string }) {
  const [shown, setShown] = useState(false);
  const { me, secrets } = view;
  const show = () => setShown(true);
  const hide = () => setShown(false);
  return (
    <button
      className={`role-card ${shown ? `is-shown is-${me.role}` : ""}`}
      onPointerDown={show}
      onPointerUp={hide}
      onPointerLeave={hide}
      onPointerCancel={hide}
      onKeyDown={(e) => (e.key === " " || e.key === "Enter") && show()}
      onKeyUp={hide}
      onContextMenu={(e) => e.preventDefault()}
      aria-label="Press and hold to see your role"
    >
      <span className="gilt-frame" style={{ "--cloak": CLOAK_COLOURS[me.cloak] } as React.CSSProperties}>
        <span className="canvas">
          {photoOf(view.castle, me.id) ? <img className="face-photo" src={photoOf(view.castle, me.id)!} alt="" /> : <img className="face-photo face-silhouette" src={silhouetteUrl(me.sigil, me.cloak)} alt="" />}
        </span>
      </span>
      {shown ? (
        <span className="role-text">
          <span className="role-name">{me.role === "traitor" ? "Unfaithful" : "Faithful"}</span>
          {me.role === "traitor" && secrets.fellowTraitors.length > 0 && (
            <span className="role-detail">With {secrets.fellowTraitors.map(name).join(" and ")}</span>
          )}
          {secrets.hasShield && <span className="role-detail">You hold the Shield</span>}
        </span>
      ) : (
        <span className="role-text">
          <span className="role-name">{me.name}</span>
          <span className="role-detail">Press and hold to see your role</span>
        </span>
      )}
    </button>
  );
}

function PlayerChoice({
  title,
  players,
  chosen = null,
  onChoose,
}: {
  title: string;
  players: PublicPlayer[];
  chosen?: string | null;
  onChoose: (id: string) => void;
}) {
  return (
    <>
      <p className="panel-lead">{title}</p>
      <ul className="choices">
        {players.map((p) => (
          <li key={p.id}>
            <button className={`choice ${chosen === p.id ? "is-chosen" : ""}`} onClick={() => onChoose(p.id)}>
              <MiniFace p={p} /> {p.name}
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

/** Faithfuls get a harmless task at night so their phones look as busy as a Traitor's. */
/** The Traitors' offer: join them, or stay Faithful. */
function RecruitOffer({ answer, run }: { answer: "accept" | "decline" | null; run: (a: GameActionInput) => Promise<unknown> }) {
  if (answer === "accept")
    return (
      <>
        <p className="panel-lead">You said yes. Keep your face still. At breakfast you'll be one of them.</p>
      </>
    );
  if (answer === "decline")
    return (
      <>
        <p className="panel-lead">You refused. You stay Faithful. Say nothing, or say everything: your call.</p>
      </>
    );
  return (
    <div className="recruit-offer">
      <p className="panel-lead">The Unfaithful have chosen you.</p>
      <p className="quiet">
        Join them and you play for the Unfaithful from now on: you'll choose who leaves at night, and you'll win if they win. Refuse and you stay Faithful.
      </p>
      <button className="primary wax-button" onClick={() => void run({ type: "recruit-answer", accept: true })}>
        Join the Unfaithful
      </button>
      <button className="secondary moon-button" onClick={() => void run({ type: "recruit-answer", accept: false })}>
        Stay Faithful
      </button>
    </div>
  );
}

/** Faithfuls choose too: the same list, the same heading, a harmless question. */
function DecoyChoice({ players, chosen, setChosen }: { players: PublicPlayer[]; chosen: string | null; setChosen: (id: string) => void }) {
  return (
    <>
      <PlayerChoice title="Choose a face." players={players} chosen={chosen} onChoose={setChosen} />
      <p className="night-hint">Someone you'd trust with your life. Nobody else will ever see it.</p>
    </>
  );
}

/**
 * The night task, the same on every phone: 1. choose a face, 2. tap the candle
 * until it's alight, 3. sleep. You can't skip a step, and the night waits for all.
 */
function NightTask({ asleep, chosen: realChosen, decoy, onSleep, children }: { asleep: boolean; chosen: boolean; decoy: PublicPlayer[] | null; onSleep: () => void; children: React.ReactNode }) {
  const [lit, setLit] = useState(false);
  // Faithful choose a face too (it goes nowhere), so every phone does the same three steps.
  const [decoyFace, setDecoyFace] = useState<string | null>(null);
  const chosen = decoy ? decoyFace !== null : realChosen;
  if (asleep) {
    return (
      <div className="night-asleep">
        <span className="night-moon" aria-hidden />
        <p className="panel-lead">You're asleep.</p>
        <p className="night-hint">Morning comes when every candle is lit.</p>
      </div>
    );
  }
  return (
    <>
      <ol className="night-steps" aria-label="Tonight">
        <li className={chosen ? "is-done" : "is-now"}>Choose</li>
        <li className={lit ? "is-done" : chosen ? "is-now" : ""}>Light the candle</li>
        <li className={lit ? "is-now" : ""}>Sleep</li>
      </ol>
      {decoy ? <DecoyChoice players={decoy} chosen={decoyFace} setChosen={setDecoyFace} /> : children}
      {chosen && <Candle onLit={() => setLit(true)} />}
      {lit && (
        <button className="primary wax-button night-sleep" onClick={onSleep}>
          Blow it out and sleep
        </button>
      )}
    </>
  );
}

/**
 * Night: the whole phone goes dark, and every phone, Traitor or Faithful, shows
 * the same kind of screen: choose a face, then keep a candle alight. From across
 * the room, everyone is tapping.
 */
function NightShroud({ children }: { children: ReactNode }) {
  return (
    <div className="night-shroud" role="region" aria-label="Night">
      <p className="night-title">Night falls. Keep your phone low and your face still.</p>
      {children}
    </div>
  );
}

/** Tap the candle until it's alight: everyone does it, so every phone is busy. */
const CANDLE_TAPS = 12;
function Candle({ onLit }: { onLit?: () => void }) {
  const [taps, setTaps] = useState(0);
  const lit = taps >= CANDLE_TAPS;
  useEffect(() => {
    if (lit) onLit?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lit]);
  const flame = Math.min(1, taps / CANDLE_TAPS);
  return (
    <div className="candle-task">
      <p className="night-hint">{lit ? "The candle is alight." : "Tap the candle to light it."}</p>
      <button className="candle" style={{ "--flame": flame } as React.CSSProperties} onClick={() => setTaps((t) => Math.min(CANDLE_TAPS, t + 1))} aria-label="Tap the candle">
        <span className="candle-flame" aria-hidden />
        <span className="candle-wax" aria-hidden />
      </button>
      <div className="candle-meter" aria-hidden>
        <span style={{ width: `${flame * 100}%` }} />
      </div>
    </div>
  );
}

/**
 * The Round Table on your phone: the floor light (whose turn it is to speak),
 * throwing heat on as many people as you like, and a live summary of every
 * accusation at this table.
 */
function RoundTable({ view, clockOffset, name, run }: { view: PlayerView; clockOffset: number; name: (id: string) => string; run: (a: GameActionInput) => Promise<void> }) {
  const { castle, me } = view;
  const game = castle.game!;
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, []);
  const alive = castle.players.filter((p) => p.status === "alive");
  const now = Date.now() + clockOffset;
  const floor = game.paused ? null : floorNow(game.floor, castle.phaseEndsAt, now);
  const secs = floor ? Math.max(0, Math.ceil((floor.turnEndsAt - now) / 1000)) : null;
  const heat = game.heat ?? [];
  const byTarget = new Map<string, { by: string; reason: string }[]>();
  for (const h of heat) byTarget.set(h.targetId, [...(byTarget.get(h.targetId) ?? []), { by: h.by, reason: h.reason }]);
  const ranked = [...byTarget.entries()].sort((a, b) => b[1].length - a[1].length);
  const mine = heat.filter((h) => h.by === me.id);

  return (
    <div className="round-table-phone">
      {floor && (
        <div className={`floor-light ${floor.speaker === me.id ? "is-you" : floor.speaker ? "is-listen" : "is-open"}`} role="status">
          <span className="floor-lamp" aria-hidden />
          <div>
            <p className="floor-main">
              {floor.waiting ? "Today's diaries first" : floor.speaker === me.id ? "You have the floor" : floor.speaker ? `Listen: ${name(floor.speaker)} is speaking` : "Open floor: anyone may speak"}
            </p>
            <p className="floor-sub">
              {secs}s{floor.nextSpeaker ? (floor.nextSpeaker === me.id ? ". You're next." : `. Next: ${name(floor.nextSpeaker)}.`) : floor.speaker ? ". Then the floor is open." : ". Then the vote."}
            </p>
          </div>
        </div>
      )}

      {floor?.speaker === me.id && (
        <button className="secondary moon-button pass-floor" onClick={() => void run({ type: "pass-floor" })}>
          Done: pass the floor on
        </button>
      )}
      {(floor?.speaker === me.id || (floor && !floor.speaker && !floor.waiting)) && (
        <Grill others={alive.filter((p) => p.id !== me.id)} asked={(game.grilling ?? []).filter((g) => g.by === me.id).length} turnKey={`${game.day}-${floor.turn}`} run={run} />
      )}
      {(() => {
        const last = (game.grilling ?? []).at(-1);
        const q = last ? GRILL_BY_ID[last.questionId] : undefined;
        if (!last || !q || last.by === me.id) return null;
        return (
          <p className={`grill-heard ${last.targetId === me.id ? "is-me" : ""}`}>
            <span>{name(last.by)} asks {last.targetId === me.id ? "you" : last.targetId ? name(last.targetId) : "the table"}:</span> “{grillText(q, last.targetId === me.id ? "you" : last.targetId ? name(last.targetId) : null)}”
          </p>
        );
      })()}

      <details className="suspect-box">
        <summary>Say who you suspect (everyone sees it)</summary>
        <Accuse players={alive.filter((p) => p.id !== me.id)} mine={mine} name={name} run={run} />
      </details>

      <section className="heat-summary" aria-label="Accusations at this table">
        <h3 className="heat-title">At this table</h3>
        {ranked.length === 0 ? (
          <p className="quiet">No one has been accused yet.</p>
        ) : (
          <ul className="heat-list">
            {ranked.map(([target, list]) => (
              <li key={target} className={target === me.id ? "is-me" : ""}>
                <p className="heat-target">
                  {target === me.id ? "You" : name(target)} <span className="heat-count">{list.length}</span>
                </p>
                <p className="heat-who">
                  {list.map((x) => `${x.by === me.id ? "you" : name(x.by)} (${x.reason.toLowerCase()})`).join(", ")}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** Throw heat: accuse as many people as you like, one reason each. */
function Accuse({
  players,
  mine,
  name,
  run,
}: {
  players: PublicPlayer[];
  mine: { targetId: string; reason: string }[];
  name: (id: string) => string;
  run: (a: GameActionInput) => Promise<void>;
}) {
  const [target, setTarget] = useState<string | null>(null);
  const [own, setOwn] = useState("");
  if (target) {
    return (
      <div className="accuse-why">
        <p className="panel-lead">Why {name(target)}?</p>
        <form
          className="own-reason"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!own.trim()) return;
            await run({ type: "accuse", targetId: target, reason: own.trim() });
            setOwn("");
            setTarget(null);
          }}
        >
          <input className="text-input" maxLength={60} placeholder="In your own words…" value={own} onChange={(e) => setOwn(e.target.value)} />
          <button className="secondary moon-button" disabled={!own.trim()}>
            Say it
          </button>
        </form>
        <p className="quiet">Or pick one:</p>
        <ul className="choices">
          {ACCUSE_CHOICES.map((r) => (
            <li key={r}>
              <button
                className="choice"
                onClick={async () => {
                  await run({ type: "accuse", targetId: target, reason: r });
                  setTarget(null);
                }}
              >
                {r}
              </button>
            </li>
          ))}
        </ul>
        <button className="text-button" onClick={() => setTarget(null)}>
          Never mind
        </button>
      </div>
    );
  }
  const accused = new Map(mine.map((m) => [m.targetId, m.reason]));
  return (
    <div className="accuse">
      <p className="panel-lead">Throw heat on anyone you suspect</p>
      <ul className="choices heat-choices">
        {players.map((p) => (
          <li key={p.id}>
            <button className={`choice ${accused.has(p.id) ? "is-chosen" : ""}`} onClick={() => setTarget(p.id)}>
              <MiniFace p={p} /> {p.name}
              {accused.has(p.id) && <span className="choice-note">{accused.get(p.id)}</span>}
            </button>
          </li>
        ))}
      </ul>
      <p className="quiet">Then say it out loud when you have the floor.</p>
    </div>
  );
}

function MissionReport({ players, run }: { players: PublicPlayer[]; run: (a: GameActionInput) => void }) {
  const [shieldTo, setShieldTo] = useState<string>("");
  return (
    <div className="report">
      <p className="panel-title">Host: how did it go?</p>
      <label className="field">
        <span className="field-label">Shield to (Flawless or Strong only)</span>
        <select className="text-input" value={shieldTo} onChange={(e) => setShieldTo(e.target.value)}>
          <option value="">Nobody</option>
          {players.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      {TIERS.map((t) => (
        <button
          key={t}
          className={t === "flawless" || t === "strong" ? "primary wax-button" : "secondary moon-button"}
          onClick={() => run({ type: "mission-report", tier: t, shieldTo: t === "flawless" || t === "strong" ? shieldTo || null : null })}
        >
          {TIER_INFO[t].label}
        </button>
      ))}
    </div>
  );
}

function useCountdown(endsAt: number | null, offset: number): number | null {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, []);
  return secondsLeft(endsAt, offset);
}

/**
 * A Shield chance on your phone. Every phone looks the same while it's
 * decided, so nobody can read a choice over a shoulder. In a Bargain, those
 * not offered the deal see a screen just like it.
 */
function ShieldChoice({ view, run, name }: { view: PlayerView; run: (a: GameActionInput) => void; name: (id: string) => string }) {
  const c = view.castle.game!.shieldChance;
  const mine = view.secrets.shield;
  if (!c || !mine) return <p className="quiet">Look up at the TV.</p>;
  const info = SHIELD_INFO[c.kind];
  const question = info.question.replace("{cost}", formatPot(c.cost));
  if (mine.myChoice !== null) {
    return (
      <div className="shield-panel">
        <p className="panel-lead">Your choice is locked in.</p>
        <p className="quiet">{info.open ? (mine.myChoice ? "Everyone saw you claim it." : "You held back.") : "Nobody will know what you chose. Keep a straight face."}</p>
        {c.kind === "one-hand" && c.claimants.length > 0 && <p className="quiet">Claimed by: {c.claimants.map(name).join(", ")}</p>}
      </div>
    );
  }
  if (c.kind === "bargain" && !mine.offeredToMe) {
    return (
      <div className="shield-panel">
        <p className="shield-panel-kicker">{info.title}</p>
        <p className="panel-lead">No offer for you this time.</p>
        <p className="quiet">Someone at this table has one. Watch their face, and keep yours still: they're watching you too.</p>
      </div>
    );
  }
  if (!mine.canDecide) return <p className="quiet">Look up at the TV.</p>;
  return (
    <div className="shield-panel">
      <p className="shield-panel-kicker">{info.title}{info.open ? ": everyone will see" : ": secret"}</p>
      <p className="panel-lead">{question}</p>
      <div className="shield-actions">
        <button className="primary wax-button" onClick={() => run({ type: "shield-choice", take: true })}>
          {info.take}
        </button>
        <button className="secondary moon-button" onClick={() => run({ type: "shield-choice", take: false })}>
          {info.leave}
        </button>
      </div>
    </div>
  );
}

/** After a Shield chance: tell the winner, quietly. */
function ShieldOutcome({ view }: { view: PlayerView }) {
  const s = view.secrets.shield;
  if (!s || s.myChoice === null) return null;
  if (s.wonIt) return <p className="shield-note is-won">The Shield is yours. It will turn the blade once. Tell nobody.</p>;
  if (s.myChoice) return <p className="shield-note">Your hand came out empty. No Shield this time.</p>;
  return null;
}

/**
 * Your turn with the floor: choose who to question (or the whole table), then
 * one of three questions. "Other questions" deals three more. The TV shows the
 * one you pick, and the Keeper reads it out.
 */
function Grill({ others, asked, turnKey, run }: { others: PublicPlayer[]; asked: number; turnKey: string; run: (a: GameActionInput) => Promise<void> }) {
  const [target, setTarget] = useState<string | null>(null);
  const [deal, setDeal] = useState(0);
  const [sent, setSent] = useState<string | null>(null);
  useEffect(() => setSent(null), [turnKey]);
  if (asked >= GRILL_LIMIT) return <p className="quiet grill-done">You've asked your questions. Listen to the answers.</p>;
  const pool = GRILL_QUESTIONS.filter((q) => q.aimed === (target !== null));
  // Three at a time, a different three each deal (and each turn).
  const seed = turnKey.length + deal * 7 + (target ? target.length : 0);
  const shown = [0, 1, 2].map((k) => pool[(seed * 5 + k * 11 + deal * 3) % pool.length]!).filter((q, i, a) => a.indexOf(q) === i);
  const targetName = target ? (others.find((p) => p.id === target)?.name ?? null) : null;
  return (
    <section className="grill" aria-label="Question the table">
      <p className="panel-lead">{sent ? "Asked. Now listen, and push back." : "Your turn: question the table."}</p>
      <p className="quiet">Who are you asking?</p>
      <div className="grill-targets" role="radiogroup">
        <button role="radio" aria-checked={target === null} className={`chip ${target === null ? "is-on" : ""}`} onClick={() => setTarget(null)}>
          Everyone
        </button>
        {others.map((p) => (
          <button key={p.id} role="radio" aria-checked={target === p.id} className={`chip ${target === p.id ? "is-on" : ""}`} onClick={() => setTarget(p.id)}>
            {p.name}
          </button>
        ))}
      </div>
      <ul className="choices grill-questions">
        {shown.map((q) => (
          <li key={q.id}>
            <button
              className="choice"
              onClick={async () => {
                await run({ type: "ask", questionId: q.id, targetId: target });
                setSent(q.id);
              }}
            >
              {grillText(q, targetName)}
            </button>
          </li>
        ))}
      </ul>
      <button className="text-button" onClick={() => setDeal((d) => d + 1)}>
        Other questions
      </button>
    </section>
  );
}

/** Welcome drinks on your phone: your Hot Seat question, your verdict on others, your Most Likely To pick. */
function Drinks({ view, clockOffset, run }: { view: PlayerView; clockOffset: number; run: (a: GameActionInput) => Promise<void> }) {
  const g = view.castle.game!.gathering;
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, []);
  if (!g) return <p className="quiet">Look up at the TV.</p>;
  const me = view.me;
  const mine = view.secrets.gathering ?? { believed: {}, picked: {} };
  const m = gatheringAt(g.plan, g.startedAt, g.paceFactor, Date.now() + clockOffset);
  const others = view.castle.players.filter((p) => p.status === "alive" && p.id !== me.id);
  const name = (id: string) => view.castle.players.find((p) => p.id === id)?.name ?? "Someone";
  if (m.part === "intro" || m.part === "done") {
    return (
      <div className="drinks-phone">
        <p className="panel-lead">Welcome drinks.</p>
        <p className="quiet">Before the first mission, a chance to size each other up. Keep your role to yourself.</p>
      </div>
    );
  }
  if (m.part === "hot") {
    const q = HOT_SEAT_BY_ID[m.questionId];
    if (m.playerId === me.id) {
      return (
        <div className="drinks-phone is-me">
          <p className="drinks-phone-kicker">You're in the Hot Seat</p>
          <p className="panel-lead">{q?.text}</p>
          <p className="quiet">Answer out loud. Everyone is deciding whether they believe you.</p>
        </div>
      );
    }
    const done = mine.believed[m.index];
    return (
      <div className="drinks-phone">
        <p className="drinks-phone-kicker">The Hot Seat: {name(m.playerId)}</p>
        <p className="panel-lead">{q?.text}</p>
        {done === undefined ? (
          <div className="shield-actions">
            <button className="primary wax-button" onClick={() => void run({ type: "gather-believe", index: m.index, believe: true })}>
              I believe them
            </button>
            <button className="secondary moon-button" onClick={() => void run({ type: "gather-believe", index: m.index, believe: false })}>
              I don't buy it
            </button>
          </div>
        ) : (
          <p className="quiet">{done ? "You believe them. For now." : "You don't buy it. Noted."}</p>
        )}
      </div>
    );
  }
  const q = MOST_LIKELY_BY_ID[m.questionId];
  const picked = mine.picked[m.index];
  return (
    <div className="drinks-phone">
      <p className="drinks-phone-kicker">Most likely to</p>
      <p className="panel-lead">{q?.text}</p>
      {m.stage === "reveal" ? (
        <p className="quiet">Look up at the TV.</p>
      ) : picked ? (
        <p className="quiet">You named {name(picked)}. Wait for the reveal.</p>
      ) : (
        <ul className="choices">
          {others.map((p) => (
            <li key={p.id}>
              <button className="choice" onClick={() => void run({ type: "gather-pick", index: m.index, targetId: p.id })}>
                <MiniFace p={p} /> {p.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The diary room is somewhere else: step out first, then record, then come back. */
function DiaryRoom({ children }: { children: ReactNode }) {
  const [inRoom, setInRoom] = useState(false);
  if (inRoom) return <>{children}</>;
  return (
    <div className="diary-room">
      <p className="panel-lead">You're in the diary room today.</p>
      <p className="quiet">Take your phone and slip out somewhere nobody can hear you. When you're alone, start. Come back when you've sent it.</p>
      <button className="primary wax-button" onClick={() => setInRoom(true)}>I'm alone: start my diary</button>
    </div>
  );
}
